import React, { useEffect, useState } from 'react';
import LoaderProposal from './components/LoaderProposal';
import NotFoundProposal from './components/NotFoundProposal';

/**
 * Página de passagem: resolve o id da proposta para o novo endpoint da API
 * Empresas e redireciona o browser. Links antigos no WhatsApp/email dos clientes
 * continuam apontando para esta URL, e a partir daqui o cliente é levado
 * à nova tela pública servida pela API.
 *
 * Fluxo:
 *   1. Lê `id` da query string.
 *   2. Chama GET {API}/proposal/public/resolve/:id (sem auth, rate-limitado).
 *   3. Recebe `view_token` e faz `window.location.replace` para a tela HTML.
 *
 * Se a proposta não for encontrada ou ainda não tiver view_token, exibe a
 * tela de "link expirado" já existente no projeto.
 */
export default function ProposalPartReq(): JSX.Element {
  const [status, setStatus] = useState<'loading' | 'not-found'>('loading');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');

    if (!id) {
      setStatus('not-found');
      return;
    }

    const apiBase = (
      process.env.REACT_APP_API_PUBLIC_URL ||
      process.env.REACT_APP_URL_API ||
      ''
    ).replace(/\/$/, '');

    if (!apiBase) {
      setStatus('not-found');
      return;
    }

    fetch(`${apiBase}/proposal/public/resolve/${encodeURIComponent(id)}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    })
      .then(async response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data: { view_token?: string } = await response.json();

        if (!data?.view_token) throw new Error('missing_token');

        window.location.replace(
          `${apiBase}/proposal/public/track/${encodeURIComponent(
            id,
          )}/${encodeURIComponent(data.view_token)}`,
        );
      })
      .catch(() => setStatus('not-found'));
  }, []);

  return (
    <div className="min-h-full">
      <div className="lg:pl-64 flex flex-col flex-1">
        <main className="flex-1 pb-8">
          <div className="mt-4 max-w-3xl mx-auto grid grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
            <div className="space-y-6 lg:col-start-1 lg:col-span-2">
              <section aria-labelledby="redirect-title">
                <div className="bg-white shadow-2xl sm:rounded-xl">
                  {status === 'loading' ? <LoaderProposal /> : <NotFoundProposal />}
                </div>
              </section>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
